import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, DoorOpen, LayoutGrid, Sun, Wind, Eye, Volume2 } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

const menuiserieExterieure = [
  "Fenêtres bois, aluminium ou mixtes",
  "Baies vitrées",
  "Porte d'entrée",
  "Façades bois ou aluminium",
  "Volets et brise-soleil",
  "Claustras"
];

const menuiserieInterieure = [
  "Portes intérieures",
  "Cloisons bois ou vitrées",
  "Verrières",
  "Escaliers",
  "Aménagements sur mesure"
];

const influences = [
  {
    icon: Sun,
    title: "Apports solaires",
    description: "Optimisation des apports passifs en hiver et protection contre la surchauffe en été"
  },
  {
    icon: Volume2,
    title: "Confort acoustique",
    description: "Étanchéité à l'air et isolation phonique pour un intérieur serein"
  },
  {
    icon: Wind,
    title: "Performance thermique",
    description: "Réduction des déperditions et des consommations énergétiques"
  },
  {
    icon: Eye,
    title: "Perception du lieu",
    description: "La qualité de vie depuis l'intérieur comme depuis l'extérieur"
  }
];

const problemesInterieurs = [
  "Perte d'intimité entre les espaces",
  "Agitation permanente difficile à expliquer",
  "Mauvaise circulation thermique et acoustique",
  "Sensation de pression ou d'inconfort diffus"
];

export const Menuiserie = () => {
  return (
    <div data-testid="menuiserie-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 sm:py-32 lg:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/7109997/pexels-photo-7109997.jpeg"
            alt="Menuiserie artisanale"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0908]/80 via-[#0a0908]/70 to-[#0a0908]"></div>
        </div>

        <motion.div 
          className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.p 
            variants={fadeInUp}
            className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#C5A059] mb-6"
          >
            Menuiserie tout support
          </motion.p>
          
          <motion.h1 
            variants={fadeInUp}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-[#fdfbf7] mb-8 leading-tight max-w-4xl"
          >
            L'organe respiratoire de{' '}
            <span className="text-[#C5A059]">votre bâtiment</span>
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            className="text-base sm:text-lg text-[#a39c93] max-w-2xl leading-relaxed font-body"
          >
            Chez Rozier Menuiserie, l'esthétique est au service de la structure. Jamais l'inverse. 
            Chaque ouverture est une décision structurelle.
          </motion.p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-24 sm:py-32 bg-[#0a0908]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#a39c93] leading-relaxed mb-10 font-body text-lg">
              La menuiserie ne se limite pas à un matériau. Elle est l'interface entre vous et votre environnement.
              Chaque ouverture, chaque porte, chaque fenêtre, chaque cloison est une décision structurelle qui influence :
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-[#1c1a17] border border-[#2a2622] p-6">
                <div className="w-3 h-3 bg-[#C5A059] rounded-full mx-auto mb-4"></div>
                <span className="text-[#fdfbf7] font-body text-sm">La performance thermique</span>
              </div>
              <div className="bg-[#1c1a17] border border-[#2a2622] p-6">
                <div className="w-3 h-3 bg-[#C5A059] rounded-full mx-auto mb-4"></div>
                <span className="text-[#fdfbf7] font-body text-sm">Le confort lumineux</span>
              </div>
              <div className="bg-[#1c1a17] border border-[#2a2622] p-6">
                <div className="w-3 h-3 bg-[#C5A059] rounded-full mx-auto mb-4"></div>
                <span className="text-[#fdfbf7] font-body text-sm">La ventilation naturelle</span>
              </div>
              <div className="bg-[#1c1a17] border border-[#2a2622] p-6">
                <div className="w-3 h-3 bg-[#C5A059] rounded-full mx-auto mb-4"></div>
                <span className="text-[#fdfbf7] font-body text-sm">La dynamique énergétique</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Menuiserie Extérieure */}
      <section className="py-24 sm:py-32 bg-[#0f0d0b]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <DoorOpen size={24} className="text-[#C5A059]" />
                <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#C5A059]">
                  L'enveloppe intelligente
                </p>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-light text-[#fdfbf7] mb-6 leading-tight">
                Menuiserie extérieure
              </h2>
              <p className="text-[#a39c93] leading-relaxed mb-8 font-body">
                Vos fenêtres, baies vitrées, porte d'entrée et façades constituent la membrane régulatrice 
                entre votre environnement extérieur et votre espace intérieur.
              </p>
              <ul className="space-y-3">
                {menuiserieExterieure.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Check size={16} className="text-[#C5A059] flex-shrink-0" />
                    <span className="text-[#a39c93] font-body">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              {influences.map((influence, index) => (
                <div key={index} className="bg-[#1c1a17] border border-[#2a2622] p-6">
                  <influence.icon size={20} className="text-[#C5A059] mb-3" />
                  <h4 className="font-heading text-[#fdfbf7] mb-2">{influence.title}</h4>
                  <p className="text-[#a39c93] text-xs leading-relaxed font-body">{influence.description}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Porte d'entrée */}
      <section className="py-24 sm:py-32 bg-[#0a0908]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div 
            className="bg-[#1c1a17] border border-[#C5A059]/30 p-8 sm:p-12"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="aspect-[4/5] overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1760385737098-0b555a75b2ba?crop=entropy&cs=srgb&fm=jpg&q=85"
                    alt="Porte d'entrée en bois"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">
                  Attention particulière
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl font-light text-[#fdfbf7] mb-6 leading-tight">
                  Votre porte d'entrée
                </h2>
                <p className="text-[#a39c93] leading-relaxed mb-6 font-body">
                  Premier élément visible de votre lieu de vie, elle en détermine la qualité d'accueil, 
                  l'axe de circulation principal et la dynamique énergétique dès le seuil franchi.
                </p>
                <p className="text-[#a39c93] leading-relaxed font-body">
                  Le choix du matériau — bois, aluminium, mixte ou composite — n'est jamais esthétique. 
                  Il est toujours cohérent avec votre projet dans sa globalité.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Menuiserie Intérieure */}
      <section className="py-24 sm:py-32 bg-[#0f0d0b]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <LayoutGrid size={24} className="text-[#C5A059]" />
                <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#C5A059]">
                  L'architecture des flux
                </p>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-light text-[#fdfbf7] mb-6 leading-tight">
                Menuiserie intérieure
              </h2>
              <p className="text-[#a39c93] leading-relaxed mb-8 font-body">
                À l'intérieur, vos portes, cloisons, verrières et escaliers structurent la hiérarchie 
                de vos espaces, la circulation de l'air, la propagation du son, la lumière naturelle 
                et la dynamique énergétique de votre lieu de vie.
              </p>
              <ul className="space-y-3">
                {menuiserieInterieure.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Check size={16} className="text-[#C5A059] flex-shrink-0" />
                    <span className="text-[#a39c93] font-body">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-[#1c1a17] border border-[#2a2622] p-8"
            >
              <h3 className="font-heading text-xl text-[#fdfbf7] mb-6">
                Une mauvaise organisation intérieure peut générer :
              </h3>
              <ul className="space-y-4">
                {problemesInterieurs.map((probleme, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#C5A059] rounded-full mt-2"></div>
                    <span className="text-[#a39c93] font-body">{probleme}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-[#2a2622]">
                <p className="text-[#a39c93] text-sm font-body italic">
                  "Une porte pleine ralentit un flux. Une verrière le fluidifie. 
                  Une cloison bois crée une intimité chaleureuse là où une cloison vitrée prolonge la lumière."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Volets Roulants */}
      <section className="py-24 sm:py-32 bg-[#0a0908]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">
              Également
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-light text-[#fdfbf7] mb-6">
              Pose de volets roulants
            </h2>
            <p className="text-[#a39c93] leading-relaxed font-body">
              Nous assurons également la fourniture et la pose de volets roulants, intégrés dans la 
              stratégie globale de votre enveloppe : protection solaire, sécurité, confort thermique et acoustique.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-[#1c1a17]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-[#fdfbf7] mb-6">
              Vos ouvertures méritent une{' '}
              <span className="text-[#C5A059]">réflexion globale</span>
            </h2>
            <p className="text-[#a39c93] max-w-2xl mx-auto mb-8 font-body">
              Vous avez un projet de construction, de rénovation ou de remplacement de menuiseries ? 
              Parlons-en et posons ensemble les bases d'un intérieur et d'une enveloppe cohérents, 
              performants et durables.
            </p>
            <Link
              to="/contact"
              data-testid="menuiserie-cta-contact"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-[#0a0908] px-8 py-4 text-sm uppercase tracking-[0.15em] hover:bg-[#d6b46e] transition-colors duration-300"
            >
              Contactez-nous
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
